import { FC } from 'react';
import { NudgeBanner } from '../../components/NudgeBanner';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { OnboardCareRecipientWizard } from '../../wizard/mini-wizard/wizards/OnboardCareRecipientWizard';
import { DrawerState } from '../../components/DrawerState';
import { CareMapDrawerHelper } from '../../caremap/CareMapDrawerHelper';

export type IEditPatientNudgeBannerProps = {
    excludeDOB?: boolean;
    onEdit: () => void;
};

export const EditPatientNudgeBanner: FC<IEditPatientNudgeBannerProps> = ({ onEdit, excludeDOB }) => {
    const caremapState = useInstance(CareMapState);
    useSubscription(() => caremapState);

    const drawerState = useInstance(DrawerState);
    useSubscription(() => drawerState);

    const onboardingWizard = useInstance(OnboardCareRecipientWizard);
    const drawerHelper = useInstance(CareMapDrawerHelper);

    const canEdit = caremapState.hasPermissionTo('put');
    if (!canEdit) {
        return null;
    }

    const patient = caremapState.getActiveCaremap()?.patient;
    if (!patient){
        return null;
    }

    const showEditNudgeBadge = caremapState.isPatientIncomplete({ excludeDOB });

    const needsToFinishCareRecipientOnboarding = caremapState.hasStartedOnboardingStep('add-care-recipient') && !caremapState.hasCompletedOnboardingStep('add-care-recipient');

    if (needsToFinishCareRecipientOnboarding){
        const continueWizard = async () => {
            const inDrawer = drawerState.isOpen();
            const fn = () => onboardingWizard.start(caremapState.getActiveCaremap()!.careMapId!);
            if (inDrawer) {
                drawerHelper.closeAndExecute(fn);
            } else {
                fn();
            }
        };
        return (
            <NudgeBanner
                buttonLabel="Continue"
                buttonClickHandler={continueWizard}
                message={`Complete ${caremapState.getPatientFirstName()}’s Care Profile to keep care info organized and accessible!`}
            />
        );
    }

    return showEditNudgeBadge &&
        <NudgeBanner
            buttonClickHandler={onEdit}
            buttonLabel="Complete Profile"
            message={`Be sure to add${!excludeDOB ? ' date of birth,' : ''} medical conditions, allergies, and contact info to keep care info organized and easily accessible!`}
        />;
};
